/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FC, PropsWithChildren } from 'react'
import { Platform } from 'react-native'
import Animated, { LinearTransition } from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { styled, useTheme } from 'styled-components/native'

import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { SnackBar } from 'ui/designSystem/Snackbar/SnackBar'
import { useSnackbarProps } from 'ui/designSystem/Snackbar/snackBar.store'

const DESKTOP_BOTTOM_OFFSET = 72

const isWeb = Platform.OS === 'web'

export const SnackBarWrapper: FC<PropsWithChildren> = ({ children }) => {
  const { isDesktopViewport } = useTheme()
  const snackbarsProps = useSnackbarProps()
  const { top } = useSafeAreaInsets()
  const hasSnackbar = snackbarsProps.length > 0

  return (
    <React.Fragment>
      {hasSnackbar ? (
        <Container top={top} pointerEvents="box-none" accessibilityViewIsModal>
          {snackbarsProps.map(
            (
              {
                onClose,
                label,
                animationDuration,
                type,
                id,
                accessibilityLabel,
                accessibilityHint,
                accessibilityActions,
                onAccessibilityTap,
                onAccessibilityAction,
              },
              index
            ) => {
              const isSnackBarError = type === 'error'
              // ALERT and STATUS communicate different levels of urgency to screen readers:
              // ALERT has an implicit assertive live region and interrupts current announcements.
              // STATUS has an implicit polite live region and waits for the current announcement to finish.
              const accessibilityRole = isSnackBarError
                ? AccessibilityRole.ALERT
                : AccessibilityRole.STATUS
              const accessibilityLiveRegion = isSnackBarError ? 'assertive' : 'polite'

              return (
                <Animated.View
                  pointerEvents="box-none"
                  accessible
                  accessibilityRole={accessibilityRole}
                  accessibilityHint={accessibilityHint}
                  accessibilityActions={accessibilityActions}
                  onAccessibilityTap={onAccessibilityTap}
                  onAccessibilityAction={onAccessibilityAction}
                  importantForAccessibility="yes"
                  accessibilityLiveRegion={accessibilityLiveRegion}
                  // React Native Web already announces the snackbar content through its accessibility role.
                  // Providing an explicit accessibilityLabel causes VoiceOver to announce the message multiple times.
                  {...(!isWeb && { accessibilityLabel })}
                  style={{ zIndex: 1000 + index }}
                  key={id}
                  layout={LinearTransition.duration(500).springify()}>
                  <SnackBar
                    variant={isDesktopViewport ? 'large' : 'default'}
                    id={id}
                    onClose={onClose}
                    label={label}
                    animationDuration={animationDuration}
                    type={type}
                  />
                </Animated.View>
              )
            }
          )}
        </Container>
      ) : null}
      {children}
    </React.Fragment>
  )
}

const Container = styled.View<{ top: number }>(({ theme, top }) => ({
  top: theme.isDesktopViewport ? undefined : top,
  bottom: theme.isDesktopViewport ? DESKTOP_BOTTOM_OFFSET : undefined,
  left: 0,
  right: 0,
  position: 'absolute',
  zIndex: 1000,
}))
